import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { CLINIC, IMAGES, whatsappLink, DEFAULT_WA_MESSAGE } from "../data/site";
import { scrollToId } from "../lib/scroll";

const LINES = [
    { text: "Il tuo nuovo sorriso", accent: false },
    { text: "nasce a Valona.", accent: true },
];

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-end overflow-hidden"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img
                    src={IMAGES.hero}
                    alt="Interno della clinica"
                    className="w-full h-[120%] object-cover"
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.14),transparent_55%)]" />
            </motion.div>

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-40"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                    className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6"
                >
                    {CLINIC.name} · {CLINIC.city}
                </motion.p>

                <h1
                    data-testid="hero-title"
                    className="font-display font-light tracking-tight text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02]"
                >
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${line.accent ? "italic text-gold" : ""}`}
                                initial={{ y: "115%" }}
                                animate={{ y: "0%" }}
                                transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: EASE }}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                    className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                    Impiantologia completa con volo, hotel e transfer inclusi.
                    Inviaci la tua panoramica su WhatsApp e ricevi un preventivo
                    gratuito entro 24 ore.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1, ease: EASE }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href={whatsappLink(DEFAULT_WA_MESSAGE)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-cta-whatsapp"
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-wa text-black text-sm font-semibold uppercase tracking-[0.12em] hover:scale-[1.03] transition-transform duration-300"
                    >
                        <MessageCircle size={18} />
                        Scrivici su WhatsApp
                    </a>
                    <button
                        data-testid="hero-cta-scopri"
                        onClick={() => scrollToId("#come-funziona")}
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full border border-white/25 text-white text-sm uppercase tracking-[0.12em] hover:border-gold hover:text-gold transition-colors duration-300"
                    >
                        Scopri come funziona
                        <ArrowDown size={16} />
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.25 }}
                    className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
                    data-testid="hero-stats"
                >
                    {[
                        ["-70%", "risparmio medio rispetto all'Italia"],
                        ["24h", "per ricevere il preventivo"],
                        ["100%", "volo, hotel e transfer inclusi"],
                    ].map(([big, small]) => (
                        <div key={small}>
                            <p className="font-display text-2xl sm:text-3xl text-gold">{big}</p>
                            <p className="text-xs uppercase tracking-[0.15em] text-white/50 mt-1">
                                {small}
                            </p>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};
